<template>
  <div class="c-guest-apply-form">
    <!-- CTA step -->
    <transition name="c-guest-apply-form__fade">
      <div v-if="step === 'cta'" class="c-guest-apply-form__cta-step">
        <button
          type="button"
          class="c-button c-button--primary c-button--large c-button--cta"
          @click="openForm"
        >
          {{ $lang("outerweb.guest-apply:cta") }}
        </button>
      </div>
    </transition>

    <!-- Form step -->
    <transition name="c-guest-apply-form__slide">
      <div v-show="step === 'form'" class="c-guest-apply-form__step">
        <h3 class="c-guest-apply-form__heading">
          {{ $lang("outerweb.guest-apply:form:heading") }}
        </h3>

        <!-- GWB-555 (SUP-142): clarifies that entering the emailed code is the
             final, required step to actually submit the application. -->
        <p class="c-guest-apply-form__helper">
          {{ $lang("outerweb.guest-apply:form:helper") }}
        </p>

        <div v-if="apiError" class="warning callout" role="alert">
          <p>{{ apiError }}</p>
        </div>

        <gwb-float-field
          v-model="form.firstName"
          type="text"
          id="guest-first-name"
          name="firstName"
          :label="$lang('outerweb.guest-apply:form:first_name')"
          :error="!!errors.firstName"
          @blur="touchField('firstName')"
        ></gwb-float-field>
        <span v-if="errors.firstName" class="is-visible form-error">{{
          errors.firstName
        }}</span>

        <gwb-float-field
          v-model="form.lastName"
          type="text"
          id="guest-last-name"
          name="lastName"
          :label="$lang('outerweb.guest-apply:form:last_name')"
          :error="!!errors.lastName"
          @blur="touchField('lastName')"
        ></gwb-float-field>
        <span v-if="errors.lastName" class="is-visible form-error">{{
          errors.lastName
        }}</span>

        <gwb-float-field
          v-model="form.email"
          type="email"
          id="guest-email"
          name="email"
          :label="$lang('outerweb.guest-apply:form:email')"
          :error="!!errors.email"
          @blur="touchField('email')"
          ref="emailField"
        ></gwb-float-field>
        <span v-if="errors.email" class="is-visible form-error">{{
          errors.email
        }}</span>

        <gwb-float-field
          v-model="form.phone"
          type="tel"
          id="guest-phone"
          name="phone"
          :initAreaCode="initialAreaCode"
          :label="$lang('outerweb.guest-apply:form:phone')"
          :error="!!errors.phone"
          @blur="touchField('phone')"
        ></gwb-float-field>
        <span v-if="errors.phone" class="is-visible form-error">{{
          errors.phone
        }}</span>

        <template v-if="skills.length > 0">
          <fieldset class="c-guest-apply-form__fieldset">
            <legend class="c-guest-apply-form__legend">
              {{ $lang("outerweb.guest-apply:form:skills") }}
            </legend>
            <div
              v-for="skill in skills"
              :key="skill.id"
              class="c-guest-apply-form__checkbox-row"
            >
              <input
                type="checkbox"
                :id="'guest-skill-' + skill.id"
                :value="skill.id"
                v-model="form.selectedSkills"
              />
              <label :for="'guest-skill-' + skill.id">{{ skill.label }}</label>
            </div>
          </fieldset>
        </template>

        <fieldset class="c-guest-apply-form__fieldset">
          <legend class="c-guest-apply-form__legend">
            {{ $lang("outerweb.guest-apply:form:shifts") }}
            <span class="c-guest-apply-form__required">*</span>
          </legend>
          <div
            v-for="period in periods"
            :key="period.id"
            class="c-guest-apply-form__checkbox-row"
          >
            <input
              type="checkbox"
              :id="'guest-period-' + period.id"
              :value="period.id"
              v-model="form.selectedShifts"
            />
            <label :for="'guest-period-' + period.id">{{ period.label }}</label>
            <!-- PLAT-32 AC-2/AC-4: THIS shift's own server-formatted icon(s). An absent/empty icons
                 array renders nothing — v-for over an empty array is a no-op. No gwbui dependency
                 (GWB-494): a small local tap/hover/focus tooltip mirroring the Alpine pattern in
                 canonical-job-card.blade.php incl. @click.away (see the document listener). Hover via
                 pointer events so a touch-synthesized mouseenter can't pre-open and fight the tap; the
                 glyph is rendered per icon type. -->
            <span
              v-for="(icon, idx) in (period.icons || [])"
              :key="period.id + '-' + idx"
              class="c-guest-apply-form__premium-icon"
              :class="'c-guest-apply-form__premium-icon--' + icon.type"
              tabindex="0"
              role="button"
              :aria-label="icon.tooltip"
              @pointerenter="iconTooltip.pointerEnter(period.id + '-' + idx, $event)"
              @pointerleave="iconTooltip.pointerLeave(period.id + '-' + idx, $event)"
              @pointerdown="iconTooltip.pointerDown($event)"
              @click.stop="iconTooltip.click(period.id + '-' + idx)"
              @focus="iconTooltip.focus(period.id + '-' + idx)"
              @blur="iconTooltip.blur(period.id + '-' + idx)"
            ><span
              class="c-guest-apply-form__premium-icon-glyph"
              aria-hidden="true"
              v-html="premiumIconSvgs[icon.type]"
            ></span><span
              v-if="iconTooltip.openKey === period.id + '-' + idx"
              class="c-guest-apply-form__premium-tooltip"
              role="tooltip"
            >{{ icon.tooltip }}</span></span>
          </div>
          <span v-if="errors.selectedShifts" class="is-visible form-error">{{
            errors.selectedShifts
          }}</span>
        </fieldset>

        <div
          class="c-guest-apply-form__checkbox-row c-guest-apply-form__checkbox-row--terms"
        >
          <input type="checkbox" id="guest-terms" v-model="form.termsAgreed" />
          <label for="guest-terms"
            >{{ $lang("outerweb.guest-apply:form:terms:before") }}
            <a :href="`/terms/applying/${jobId}`" target="_blank">{{
              $lang("outerweb.guest-apply:form:terms:link")
            }}</a></label
          >
        </div>
        <span v-if="errors.termsAgreed" class="is-visible form-error">{{
          errors.termsAgreed
        }}</span>

        <div class="c-guest-apply-form__actions">
          <button
            type="button"
            class="c-button c-button--primary c-button--large"
            :disabled="loading"
            @click="submitForm"
          >
            <span v-if="loading">{{
              $lang("outerweb.guest-apply:form:submitting")
            }}</span>
            <span v-else>{{ $lang("outerweb.guest-apply:form:submit") }}</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- OTP step -->
    <transition name="c-guest-apply-form__slide">
      <div v-show="step === 'otp'" class="c-guest-apply-form__step">
        <!-- GWB-555 (SUP-142): reinforces that this code step — not the
             details form — is what actually submits the application. -->
        <p class="c-guest-apply-form__otp-subheader">
          {{ $lang("outerweb.guest-apply:otp:subheader") }}
        </p>

        <!-- GWB-576 review C2: the address is ALWAYS confirmed — the banner is gated only
             on bannedMessage, not otpPriming. The prefix reflects the send state: while a
             code has not (yet) been confirmed sent (otpPriming true — in-flight, failed, or
             bot-skipped) it reads "a code will be sent to X" (ready:banner_prefix); once a
             send is confirmed it reads "we sent a code to X" (otp:banner_prefix). Both keys
             are translated in the active locale set for this flow (en/et/lt/lv/ru). de/no
             carry only a dev-placeholder for the sibling otp:banner_prefix and no ready key
             at all — a pre-existing gap in non-served markets, not covered here. -->
        <div
          v-if="!bannedMessage && !terminalError"
          class="c-guest-apply-form__banner"
          role="status"
        >
          {{ otpPriming
            ? $lang("outerweb.guest-apply:ready:banner_prefix")
            : $lang("outerweb.guest-apply:otp:banner_prefix") }}
          <strong>{{ form.email }}</strong>
        </div>

        <div
          v-if="bannedMessage"
          class="c-guest-apply-form__banner"
          role="alert"
        >
          {{ bannedMessage }}
        </div>

        <one-time-code-input
          ref="otpCode"
          v-model="otp.code"
          input-id="guest-apply-otp-input"
          class="c-guest-apply-form__otp-inputs"
        />

        <span
          v-if="otp.errorMessage"
          class="is-visible form-error c-guest-apply-form__otp-error"
          role="alert"
        >
          {{ otp.errorMessage }}
        </span>
        <span
          v-else-if="otp.infoMessage"
          class="c-guest-apply-form__otp-info"
          role="status"
        >
          {{ otp.infoMessage }}
        </span>

        <!-- GWB-411: the whole resend affordance disappears while hard-locked —
             the otp_locked error message explains why. -->
        <div v-if="!locked" class="c-guest-apply-form__resend">
          {{ $lang("outerweb.guest-apply:otp:resend_prompt") }}
          <span v-if="!canResend" class="c-guest-apply-form__resend-link--disabled">{{
            $lang("outerweb.guest-apply:otp:resend_with_countdown", {
              countdown: countdownFormatted,
            })
          }}</span>
          <a v-else href="#" @click.prevent="resendOtp">{{
            $lang("outerweb.guest-apply:otp:resend_link")
          }}</a>
        </div>

        <div class="c-guest-apply-form__actions">
          <button
            type="button"
            class="c-button c-button--primary c-button--large"
            :disabled="loading || !otpComplete"
            @click="submitOtp"
          >
            <span v-if="loading">{{
              $lang("outerweb.guest-apply:otp:verifying")
            }}</span>
            <span v-else>{{ $lang("outerweb.guest-apply:otp:verify") }}</span>
          </button>

          <button
            v-if="entryMode === 'marketing'"
            type="button"
            class="c-button c-button--outline c-button--large"
            :disabled="loading"
            @click="openEmailModal"
          >
            {{ $lang("outerweb.guest-apply:otp:use_another_email") }}
          </button>
          <button
            v-else
            type="button"
            class="c-button c-button--outline c-button--large"
            :disabled="loading"
            @click="backToForm"
          >
            {{ $lang("outerweb.guest-apply:otp:back") }}
          </button>
        </div>

        <!-- Trust badge (D3) -->
        <div class="c-guest-apply-form__trust">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <span>{{ $lang("outerweb.guest-apply:otp:trust") }}</span>
        </div>

        <!-- Application summary -->
        <aside class="c-workbite-details__aside c-guest-apply-form__summary">
          <div v-if="employerName" class="c-guest-apply-form__summary-row">
            <strong>{{
              $lang("outerweb.guest-apply:summary:employer")
            }}</strong>
            {{ employerName }}
          </div>
          <div v-if="jobTitle" class="c-guest-apply-form__summary-row">
            <strong>{{ $lang("outerweb.guest-apply:summary:job") }}</strong>
            {{ jobTitle }}
          </div>
          <div v-if="location" class="c-guest-apply-form__summary-row">
            <strong>{{
              $lang("outerweb.guest-apply:summary:location")
            }}</strong>
            {{ location }}
          </div>
          <div v-if="rate" class="c-guest-apply-form__summary-row">
            <strong>{{ $lang("outerweb.guest-apply:summary:rate") }}</strong>
            {{ rate }}
            <!-- PLAT-32 AC-2/AC-4: the aggregate premium icon(s) across the guest's selected/
                 applyable shifts (N2, aggregate=true). Absent/empty ⇒ no icon. Same tap/hover/focus
                 tooltip + glyph as the shift-list icons above. -->
            <span
              v-for="(icon, idx) in premiumSummaryIcons"
              :key="'summary-' + idx"
              class="c-guest-apply-form__premium-icon"
              :class="'c-guest-apply-form__premium-icon--' + icon.type"
              tabindex="0"
              role="button"
              :aria-label="icon.tooltip"
              @pointerenter="iconTooltip.pointerEnter('summary-' + idx, $event)"
              @pointerleave="iconTooltip.pointerLeave('summary-' + idx, $event)"
              @pointerdown="iconTooltip.pointerDown($event)"
              @click.stop="iconTooltip.click('summary-' + idx)"
              @focus="iconTooltip.focus('summary-' + idx)"
              @blur="iconTooltip.blur('summary-' + idx)"
            ><span
              class="c-guest-apply-form__premium-icon-glyph"
              aria-hidden="true"
              v-html="premiumIconSvgs[icon.type]"
            ></span><span
              v-if="iconTooltip.openKey === 'summary-' + idx"
              class="c-guest-apply-form__premium-tooltip"
              role="tooltip"
            >{{ icon.tooltip }}</span></span>
          </div>
          <div v-if="selectedPeriodLabels.length" class="c-guest-apply-form__summary-row">
            <strong>{{ $lang("outerweb.guest-apply:summary:shifts") }}</strong>
            <div v-for="label in selectedPeriodLabels" :key="label">{{ label }}</div>
          </div>
        </aside>

        <!-- Change-email modal (marketing "use another email"). The app's own modal,
             never prompt()/confirm() — those can be globally suppressed and are untestable. -->
        <div
          v-if="emailModalOpen"
          class="c-guest-apply-form__modal-backdrop"
          @click.self="closeEmailModal"
        >
          <div
            class="c-guest-apply-form__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guest-modal-heading"
          >
            <h4 id="guest-modal-heading" class="c-guest-apply-form__modal-heading">
              {{ $lang("outerweb.guest-apply:modal:heading") }}
            </h4>

            <gwb-float-field
              v-model="modalEmail"
              type="email"
              id="guest-modal-email"
              name="modalEmail"
              :label="$lang('outerweb.guest-apply:modal:email_label')"
              :error="!!modalError"
              @keyup.enter="submitEmailModal"
            ></gwb-float-field>
            <span v-if="modalError" class="is-visible form-error">{{ modalError }}</span>

            <div class="c-guest-apply-form__actions">
              <button
                type="button"
                class="c-button c-button--primary c-button--large"
                :disabled="modalLoading"
                @click="submitEmailModal"
              >
                <span v-if="modalLoading">{{
                  $lang("outerweb.guest-apply:modal:sending")
                }}</span>
                <span v-else>{{ $lang("outerweb.guest-apply:modal:send") }}</span>
              </button>
              <button
                type="button"
                class="c-button c-button--outline c-button--large"
                :disabled="modalLoading"
                @click="closeEmailModal"
              >
                {{ $lang("outerweb.guest-apply:modal:cancel") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import GwbFloatField from "./gwb-float-field.vue";
import { createPremiumIconTooltip } from "./premium-icon-tooltip";
import OneTimeCodeInput from "./one-time-code-input.vue";
import { isCompleteCode } from "../one-time-code";
import {
  track as mixpanelTrack,
  identify as mixpanelIdentify,
  setUserProperties as mixpanelSetUserProperties,
  MixpanelEvent,
} from "../mixpanel";

// PLAT-32 AC-2 — the premium glyphs, mirroring the sibling Blade renderer
// (resources/outerweb/views/components/premium-indicators.blade.php maps night→moon,
// holiday→calendar-star). Inlined per icon type because the outerweb stack has no gwbui icon
// component (GWB-494); copied verbatim from ui/src/assets/icons/{moon,calendar-star}.svg. Rendered
// via v-html into a plain span (not directly into an <svg>, which mis-namespaces innerHTML in some
// browsers). Static, developer-authored markup — no user data — so v-html carries no XSS surface.
const PREMIUM_ICON_SVGS = {
  night:
    '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="m27.6 2.4c1.1 0 2.2.1 3.2.3-5.3 3.6-8.7 9.7-8.7 16.5 0 11 8.9 19.9 19.9 19.9 1.2 0 2.4-.1 3.6-.3-3.9 4.4-9.6 7.2-15.9 7.2-11.7 0-21.2-9.5-21.2-21.2s9.5-21.2 21.2-21.2z" fill-rule="nonzero"/></svg>',
  holiday:
    '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><g fill-rule="nonzero"><path d="m43.29 5.345v5.806c0 2.624-2.129 4.739-4.753 4.739h-2.998c-2.625 0-4.782-2.115-4.782-4.739v-5.827h-13.514v5.827c0 2.624-2.157 4.739-4.781 4.739h-2.999c-2.624 0-4.753-2.115-4.753-4.739v-5.806c-2.293.069-4.178 1.966-4.178 4.299v34.028c0 2.376 1.926 4.328 4.303 4.328h38.33c2.373 0 4.303-1.956 4.303-4.328v-34.028c0-2.333-1.885-4.23-4.178-4.299zm-1.392 36.208c0 1.027-.833 1.86-1.861 1.86h-32.156c-1.028 0-1.86-.833-1.86-1.86v-17.577c0-1.027.832-1.86 1.86-1.86h32.156c1.028 0 1.86.833 1.86 1.86z"/><path d="m9.452 12.777h2.966c.9 0 1.63-.728 1.63-1.629v-9.518c0-.901-.73-1.63-1.63-1.63h-2.966c-.9 0-1.63.729-1.63 1.63v9.518c0 .901.73 1.629 1.63 1.629z"/><path d="m35.501 12.777h2.965c.9 0 1.63-.728 1.63-1.629v-9.518c0-.901-.73-1.63-1.63-1.63h-2.965c-.901 0-1.63.729-1.63 1.63v9.518c0 .901.729 1.629 1.63 1.629z"/><path d="m24 24.2 2.35 5.1 5.58.63-4.14 3.8 1.12 5.5-4.91-2.76-4.91 2.76 1.12-5.5-4.14-3.8 5.58-.63z"/></g></svg>',
};

export default {
  name: "GuestApplyForm",

  components: {
    GwbFloatField,
    OneTimeCodeInput,
  },

  props: {
    jobId: { type: Number, required: true },
    jobTitle: { type: String, default: "" },
    employerName: { type: String, default: "" },
    location: { type: String, default: "" },
    rate: { type: String, default: "" },
    periods: { type: Array, default: () => [] },
    skills: { type: Array, default: () => [] },
    // PLAT-32 AC-2: server-formatted `{type, tooltip}[]` for the summary rate line's aggregate
    // icon(s) (N2, aggregate=true) — absent/empty ⇒ no icon (AC-4).
    premiumSummaryIcons: { type: Array, default: () => [] },
    // GWB-411: ISO-2 country of the job (LT | LV) — user_country on the
    // guest_application:* Mixpanel events.
    userCountry: { type: String, default: "" },

    // ─── ST-4 (GWB-485) marketing entry ──────────────────────────────────────
    // All default-preserving: with none of these set, the released job-detail flow
    // is byte-for-byte unchanged. entryMode gates every marketing-only branch.
    entryMode: { type: String, default: "job_detail" },
    initialStep: { type: String, default: "cta" },
    intakeToken: { type: String, default: "" },
    // Seeds the existing data.guestApplicationId (can't reuse the name — Vue
    // prop/data collision). null default = today's "no application yet".
    initialGuestApplicationId: { type: Number, default: null },
    // { firstName, lastName, email, phone } — seeds form when landing on OTP.
    prefill: { type: Object, default: null },
  },

  data() {
    // prefill is null in job-detail → every field falls back to "" (today's empty form).
    const prefill = this.prefill || {};
    return {
      step: this.initialStep,
      guestApplicationId: this.initialGuestApplicationId,
      form: {
        firstName: prefill.firstName || "",
        lastName: prefill.lastName || "",
        email: prefill.email || "",
        phone: prefill.phone || "",
        selectedShifts: [],
        selectedSkills: [],
        termsAgreed: false,
      },
      otp: {
        code: "",
        errorMessage: "",
        infoMessage: "",
      },
      errors: {},
      apiError: "",
      loading: false,
      bannedMessage: "",
      countdown: 45,
      countdownInterval: null,
      canResend: false,
      // GWB-576: "no code has been sent yet." Seeded from entryMode so the FIRST paint
      // is already truthful: marketing entry primes on mount (a code will be sent), so it
      // must start true — a bare `false` default would flash "we sent a code to X" for one
      // render before mounted() flips it (GWB-576 review). Job-detail entry sends inline
      // via store() before the OTP step shows, so false is correct there. Thereafter set
      // true at the start of the prime POST and — deliberately — left true if that prime
      // fails or is bot-skipped (not merely "while the POST is in flight"): the banner
      // prefix is gated on it, so it never asserts a send that has not happened. A
      // confirmed send (or a later successful resend) clears it.
      otpPriming: this.entryMode === "marketing",
      // GWB-576 review C3: set true only in a terminal-race prime failure
      // (not_found/already_linked/expired) — the token died between the GET and this
      // POST, so the flow is dead. Gates the address banner off, since "a code will be
      // sent to X" is false when none ever will be and there is no resend to recover.
      terminalError: false,
      // GWB-411 hard lockout: 5 failed OTP guesses hide the resend link until a
      // fresh code is minted via a new form submission.
      locked: false,
      // Change-email modal (marketing "use another email"); inert in job-detail.
      emailModalOpen: false,
      modalEmail: "",
      modalError: "",
      modalLoading: false,
      // PLAT-32 AC-2 — the premium-icon tooltip state machine (open key + touch/hover/focus
      // composition). Extracted to premium-icon-tooltip.js so the tap race + click-away are
      // unit-testable without an SFC mount (GWB-667).
      iconTooltip: createPremiumIconTooltip(),
    };
  },

  computed: {
    // PLAT-32 AC-2 — glyph markup per icon type; see PREMIUM_ICON_SVGS.
    premiumIconSvgs() {
      return PREMIUM_ICON_SVGS;
    },

    countdownFormatted() {
      return "00:" + String(this.countdown).padStart(2, "0");
    },

    otpComplete() {
      return isCompleteCode(this.otp.code);
    },

    selectedPeriodLabels() {
      return this.periods
        .filter((p) => this.form.selectedShifts.indexOf(p.id) !== -1)
        .map((p) => p.label);
    },

    initialAreaCode() {
      // Lithuania-first launch — seed the country-code selector with +370 so
      // the user only has to type the local digits. They can still pick any
      // other code from the dropdown.
      return "+370";
    },
  },

  mounted() {
    // PLAT-32 AC-2 — close-on-outside-tap for the premium tooltips (the @click.away half of the ported
    // Alpine pattern). Registered for ALL entry modes and before the marketing early return, since the
    // tooltips render on the job-detail flow too.
    document.addEventListener("click", this.onIconTooltipDocumentClick);

    // Marketing entry only. The released job-detail flow has no further mounted work, so this
    // early return keeps it byte-for-byte unchanged.
    if (this.entryMode !== "marketing") return;

    // The shifts were chosen on the marketing site; pre-select them so the summary
    // lists them (the user lands directly on OTP and can't re-pick shifts here).
    this.form.selectedShifts = this.periods.map((p) => p.id);

    // GWB-576: the OTP is no longer sent on the server continue GET (link-preview
    // bots were triggering real sends). Mint+send it now, from real page JS — a bot
    // that doesn't execute this never gets a code.
    //
    // otp_screen_viewed must wait for the prime to settle: the funnel endpoint's
    // IDOR gate matches guest_application_id against the session, and GWB-576
    // moved that session seed into primeOtp() — an event POSTed alongside the
    // prime races it, loses (the prime does DB writes + a mail send), and is
    // 403-rejected. That silently killed every marketing otp_screen_viewed row
    // from 2026-08-12 on. finally(): the screen was viewed regardless of the
    // send outcome; on a terminal prime failure (unseeded session) the event is
    // still refused server-side, which is correct — the flow is dead.
    this.primeOtp().finally(() => {
      this.trackFunnelEvent("otp_screen_viewed");
    });

    // GWB-561: scroll the OTP section into view before focusing — mirrors
    // openForm(). Focus alone can leave the digit boxes out of the viewport.
    this.scrollToOtpAndFocus();
  },

  beforeDestroy() {
    this.stopCountdown();
    document.removeEventListener("click", this.onIconTooltipDocumentClick);
  },

  methods: {
    // PLAT-32 AC-2 — a tap/click anywhere outside a premium icon closes any open tooltip, mirroring the
    // @click.away in canonical-job-card.blade.php that the in-component tooltip otherwise lacked. The
    // icon's own @click is @click.stop, so a tap ON an icon never reaches this listener — only outside
    // taps close.
    onIconTooltipDocumentClick(event) {
      const target = event.target;
      if (target && target.closest && target.closest(".c-guest-apply-form__premium-icon")) {
        return;
      }
      this.iconTooltip.closeAway();
    },

    // ─── CTA step ─────────────────────────────────────────────────────────────

    openForm() {
      // cta_clicked is recorded BEFORE the form opens — distinct from
      // form_opened which fires after the step transition + scroll. The two
      // events together let us measure scroll-into-view drop-off if needed.
      this.trackFunnelEvent("cta_clicked");
      this.step = "form";
      this.trackFunnelEvent("form_opened");
      // GWB-411: unreachable in marketing mode (the CTA never renders there),
      // so guest_application:started correctly never fires on that path.
      mixpanelTrack(
        MixpanelEvent.GUEST_APPLICATION_STARTED,
        this.guestMixpanelProps()
      );
      this.$nextTick(() => {
        this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },

    // ─── Form step ────────────────────────────────────────────────────────────

    touchField(field) {
      this.validateField(field);
    },

    validateField(field) {
      const v = this.form;
      let error = null;

      switch (field) {
        case "firstName":
          if (!v.firstName.trim())
            error = this.$lang(
              "outerweb.guest-apply:validation:first_name_required"
            );
          break;
        case "lastName":
          if (!v.lastName.trim())
            error = this.$lang(
              "outerweb.guest-apply:validation:last_name_required"
            );
          break;
        case "email":
          if (!v.email.trim()) {
            error = this.$lang(
              "outerweb.guest-apply:validation:email_required"
            );
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) {
            error = this.$lang("outerweb.guest-apply:validation:email_invalid");
          }
          break;
        case "phone":
          if (!v.phone.trim()) {
            error = this.$lang(
              "outerweb.guest-apply:validation:phone_required"
            );
          } else if (!/^\+[0-9 ]{7,20}$/.test(v.phone.trim())) {
            // Match LT worker signup format (sign-up-form.vue:838) so guests
            // are held to the same standard as the rest of the worker flow.
            error = this.$lang("outerweb.guest-apply:validation:phone_invalid");
          }
          break;
        case "selectedShifts":
          if (v.selectedShifts.length === 0)
            error = this.$lang(
              "outerweb.guest-apply:validation:shifts_required"
            );
          break;
        case "termsAgreed":
          if (!v.termsAgreed)
            error = this.$lang(
              "outerweb.guest-apply:validation:terms_required"
            );
          break;
      }

      if (error) {
        this.$set(this.errors, field, error);
      } else {
        this.$delete(this.errors, field);
      }

      return !error;
    },

    validateForm() {
      const fields = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "selectedShifts",
        "termsAgreed",
      ];
      let valid = true;
      fields.forEach((field) => {
        if (!this.validateField(field)) valid = false;
      });
      return valid;
    },

    submitForm() {
      if (!this.validateForm()) return;

      this.loading = true;
      this.apiError = "";

      this.$http
        .post("/guest-applications", {
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          email: this.form.email,
          phone: this.form.phone,
          job_id: this.jobId,
          shift_ids: this.form.selectedShifts,
          skills: this.form.selectedSkills,
          terms_agreed: this.form.termsAgreed,
        })
        .then((response) => {
          const isResubmit = this.guestApplicationId !== null;
          // otp_minted=false only means an innocent double-submit now (a live
          // UNLOCKED code already exists, no mail sent): a locked row always
          // mints fresh on resubmit (GWB-411 AC #3) or errors (429). Only a
          // real mint clears the lockout, shows "code sent", and counts as a
          // submission with an OTP email queued (AC #6).
          const otpMinted = response.data.otp_minted !== false;
          this.guestApplicationId = response.data.guest_application_id;
          this.step = "otp";
          this.countdown = 45;
          this.canResend = false;
          if (otpMinted) {
            this.locked = false;
            mixpanelTrack(
              MixpanelEvent.GUEST_APPLICATION_SUBMITTED,
              this.guestMixpanelProps({
                shifts_selected_count: this.form.selectedShifts.length,
              })
            );
          }
          if (isResubmit && otpMinted) {
            this.otp.infoMessage = this.$lang("outerweb.guest-apply:errors:resend_success");
          }
          this.startCountdown();
          this.trackFunnelEvent("otp_screen_viewed");
          // GWB-561: scroll the OTP section into view before focusing —
          // mirrors openForm(). Focus alone can leave the digit boxes out
          // of the viewport.
          this.scrollToOtpAndFocus();
        })
        .catch((error) => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          const code = data && data.error;
          if (status === 409 && code === "already_linked") {
            this.apiError = this.$lang(
              "outerweb.guest-apply:errors:already_linked"
            );
          } else if (status === 422 && data && data.errors) {
            const map = {
              first_name: "firstName",
              last_name: "lastName",
              email: "email",
              phone: "phone",
              shift_ids: "selectedShifts",
              terms_agreed: "termsAgreed",
            };
            Object.entries(data.errors).forEach(([key, messages]) => {
              const field = map[key] || key;
              this.$set(
                this.errors,
                field,
                Array.isArray(messages) ? messages[0] : messages
              );
            });
          } else if (status === 403 || code === "not_available") {
            this.apiError = this.$lang(
              "outerweb.guest-apply:errors:not_available"
            );
          } else if (status === 429 || code === "rate_limited") {
            // Per-address hourly cap, or the resend window on a locked
            // resubmit (GWB-411).
            this.apiError = this.$lang(
              "outerweb.guest-apply:errors:rate_limited"
            );
          } else if (status === 503 || code === "email_failed") {
            this.apiError = this.$lang(
              "outerweb.guest-apply:errors:email_failed"
            );
          } else {
            this.apiError = this.$lang("outerweb.guest-apply:errors:generic");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // ─── OTP step ─────────────────────────────────────────────────────────────

    /**
     * GWB-561: when the OTP step is shown, scroll it back into view and focus
     * the first digit box. The explicit scrollIntoView mirrors openForm();
     * preventScroll stops the browser's default focus-scroll from fighting
     * the smooth scroll.
     */
    scrollToOtpAndFocus() {
      this.$nextTick(() => {
        this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        if (this.$refs.otpCode) this.$refs.otpCode.focus({ preventScroll: true });
      });
    },

    submitOtp() {
      if (!this.otpComplete || this.loading) return;

      this.loading = true;
      this.otp.errorMessage = "";
      this.otp.infoMessage = "";
      this.bannedMessage = "";

      this.$http
        .post(`/guest-applications/${this.guestApplicationId}/verify-otp`, {
          code: this.otp.code,
        })
        .then((response) => {
          // sendBeacon: navigation follows immediately — a plain XHR would be
          // aborted by the redirect (same pattern as the OAuth signup anchors).
          mixpanelTrack(
            MixpanelEvent.GUEST_APPLICATION_ACCOUNT_LINKED,
            this.guestMixpanelProps({
              is_new_account: response.data.is_new_account,
            }),
            { transport: "sendBeacon" }
          );

          // GWB-411: a guest-created account IS a signup — mirror
          // sign-up-form's trackSignupCompleted (identify → people.set → track).
          if (response.data.is_new_account && response.data.mixpanel) {
            mixpanelIdentify(response.data.mixpanel.user_id);
            const userProperties = response.data.mixpanel.user_properties || {};
            mixpanelSetUserProperties(userProperties);
            mixpanelTrack(MixpanelEvent.SIGNUP_COMPLETED, userProperties, {
              transport: "sendBeacon",
            });
          }

          // GWB-589: b2c_signup_success (genuinely-new accounts only) and b2c_workbit_applied for
          // the guest apply path — the one apply surface that creates Application rows without a
          // logged-in session, and the last uncovered one after GWB-588. An ordered LIST because this
          // single response can legitimately be an identity push, a signup and an apply; the server
          // decides which entries exist AND in what order, so there is no gate and no ordering
          // decision here. Pushed before the redirect on purpose: gtm.js handles a dataLayer push
          // synchronously, unlike the Mixpanel calls above, which need sendBeacon because their XHR
          // would be aborted by the navigation.
          if (response.data.ga4) {
            window.dataLayer = window.dataLayer || [];
            response.data.ga4.forEach((event) => window.dataLayer.push(event));
          }

          window.location.href = response.data.redirect;
        })
        .catch((error) => {
          const status = error.response && error.response.status;
          const data = error.response && error.response.data;
          const errorCode = data && data.error;

          // GWB-411 hard lockout: hide the resend link until a fresh submission.
          if (errorCode === "otp_locked") {
            this.locked = true;
            this.stopCountdown();
            this.canResend = false;
          }

          // Banned account: surface the banner and clear digits so the user
          // doesn't keep retrying.
          if (errorCode === "banned") {
            this.bannedMessage = this.$lang(
              "outerweb.guest-apply:errors:banned"
            );
            this.clearOtpDigits();
            this.loading = false;
            return;
          }

          // Codes that mean "this code is gone or this session is invalid";
          // wipe the digits so the user types fresh.
          const wipeDigitsCodes = [
            "invalid_code",
            "otp_expired",
            "otp_locked",
            "forbidden",
            "invalid_state",
          ];

          // Map wire codes to localised copy. link_failed deliberately omitted
          // from wipeDigitsCodes: the OTP is still valid, the user just needs
          // to press Continue again.
          const messageKey = {
            invalid_code: "outerweb.guest-apply:errors:invalid_code",
            otp_expired: "outerweb.guest-apply:errors:otp_expired",
            otp_locked: "outerweb.guest-apply:errors:otp_locked",
            otp_error: "outerweb.guest-apply:errors:otp_generic",
            link_failed: "outerweb.guest-apply:errors:link_failed",
            forbidden: "outerweb.guest-apply:errors:forbidden",
            invalid_state: "outerweb.guest-apply:errors:invalid_state",
          }[errorCode];

          if (messageKey) {
            this.otp.errorMessage = this.$lang(messageKey);
            if (wipeDigitsCodes.indexOf(errorCode) !== -1) {
              this.clearOtpDigits();
            }
          } else if (status === 403) {
            // Defensive: any 403 without a recognised code is still a session
            // ownership failure.
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:forbidden"
            );
            this.clearOtpDigits();
          } else {
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:generic"
            );
          }

          this.loading = false;
        });
    },

    clearOtpDigits() {
      this.otp.code = "";
      // Same scroll+focus treatment as the other OTP entry points, so the
      // digit boxes (and the error message above them) stay in view when an
      // invalid code wipes the inputs.
      this.scrollToOtpAndFocus();
    },

    // GWB-576: mint+send the OTP once the marketing page is actually mounted in a
    // real browser. The server continue GET only seeds the session + renders, so a
    // link-preview bot that never runs this JS never triggers a real code/email.
    primeOtp() {
      this.otp.errorMessage = "";
      this.otp.infoMessage = "";
      // Suppress the "we sent a code to X" banner until the send is confirmed —
      // on mount the POST has not happened yet, and it may fail (GWB-576 review).
      this.otpPriming = true;

      // Returned so mounted() can sequence otp_screen_viewed after the server has
      // seeded the verifier session (see the mounted() comment). The catch below
      // handles every rejection, so the returned chain always resolves.
      return this.$http
        .post("/apply/prime-otp", { token: this.intakeToken })
        .then((response) => {
          const data = response?.data || {};
          if (data.bot === true) {
            // Bot-UA misfire (GWB-576 review): the server skipped the send for a
            // suspected bot and returned {ok:true, sent:false, bot:true}. NOTHING was
            // minted. A real human wrongly caught by BOT_UA_MARKERS lands here. Keep
            // otpPriming true so the banner reads "a code will be sent to X" (the truthful
            // ready-state — no send has happened) rather than "we sent a code to X", and
            // arm resend so they get a code by hand (resend-otp is not bot-gated; the
            // session was already seeded server-side). Show NO error: the earlier code
            // asserted a "resend failed" that never happened (GWB-576 review C2) — the
            // always-shown address banner plus the standard "Didn't receive the code?
            // Resend" prompt carry the recovery path without a misleading failure message.
            this.canResend = true;
            return;
          }
          // A live code exists — either minted just now (sent:true) or already live
          // from an earlier prime on reload (sent:false, no bot flag). Reveal the
          // address banner either way; it is truthful (a code was sent to this address).
          this.otpPriming = false;
          this.stopCountdown();
          if (data.sent === true) {
            // Fresh send: start the full 45s resend lockout.
            this.countdown = 45;
            this.canResend = false;
            this.startCountdown();
          } else {
            // Reload with a live code, no new mail (GWB-576 review B2): do NOT restart
            // a fresh 45s lockout — arm resend now, so a user whose earlier code never
            // arrived can request a new one immediately instead of waiting again.
            this.canResend = true;
          }
        })
        .catch((error) => {
          // Send failed. otpPriming stays true (no confirmed send), so the address banner
          // keeps its truthful "a code will be sent to X" prefix while the error message
          // renders alongside it — the user sees the problem, not a false success.
          const status = error?.response?.status;
          const code = error?.response?.data?.error;

          const messageKey = {
            rate_limited: "outerweb.guest-apply:errors:rate_limited",
            email_failed: "outerweb.guest-apply:errors:email_failed",
            // Terminal states — a resend won't help (unlike rate_limited/email_failed,
            // which a later resend can clear). continuePage() already validated the token
            // on the GET, so these only occur in the rare race where the token expires or
            // the app links between the GET and this mount-time POST. Generic copy is
            // honest; dedicated copy would need new lang keys (Sheet). GWB-576 review C3.
            not_found: "outerweb.guest-apply:errors:generic",
            already_linked: "outerweb.guest-apply:errors:generic",
            expired: "outerweb.guest-apply:errors:generic",
          }[code];

          // Terminal race: the token died between the GET and this mount-time POST, so the
          // flow is dead — no resend can recover it. Suppress the address banner in this
          // state (GWB-576 review C3): "a code will be sent to X" is actively false when no
          // code will ever be sent, and address confirmation is moot on a dead flow. Only
          // the generic error remains, so the screen is honest rather than contradictory.
          const terminal =
            code === "not_found" ||
            code === "already_linked" ||
            code === "expired";
          if (terminal) {
            this.terminalError = true;
          }

          if (messageKey) {
            this.otp.errorMessage = this.$lang(messageKey);
          } else if (status === 429) {
            // A 429 with no error code is the route's throttle:10,1 middleware (never
            // entered the controller, so no `seeded` field) — surface the rate-limit copy.
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:rate_limited"
            );
          } else {
            // Bare network / unknown failure on the mount-time prime. This is the FIRST
            // send, so "Could not resend the code" would be inaccurate — use the generic
            // "Something went wrong. Please try again." instead (GWB-576 review).
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:generic"
            );
          }

          // Arm resend ONLY when the server actually seeded the verifier session this
          // request — the resend button authenticates on that session, so arming it
          // unseeded would 403. The backend tells us authoritatively via `seeded` on
          // every primeOtp response (GWB-576 review): terminal codes return before the
          // seed (seeded:false), rate_limited/email_failed are thrown after it
          // (seeded:true). We do NOT infer it from the HTTP status: the route's
          // throttle:10,1 middleware can return a 429 that never entered the controller
          // and never seeded — that response has no `seeded` field, so this reads
          // undefined !== true → false, correctly leaving resend disarmed. A network
          // failure (no response body) is likewise correctly treated as unseeded.
          this.canResend = error?.response?.data?.seeded === true;
        });
    },

    resendOtp() {
      this.otp.errorMessage = "";
      this.otp.infoMessage = "";

      this.$http
        .post(`/guest-applications/${this.guestApplicationId}/resend-otp`)
        .then(() => {
          // A code did go out now — clear any suppression left by a failed prime
          // so the "sent to X" banner reflects reality again (GWB-576).
          this.otpPriming = false;
          this.stopCountdown();
          this.countdown = 45;
          this.canResend = false;
          this.otp.infoMessage = this.$lang(
            "outerweb.guest-apply:errors:resend_success"
          );
          this.startCountdown();
          mixpanelTrack(
            MixpanelEvent.GUEST_APPLICATION_OTP_RESENT,
            this.guestMixpanelProps()
          );
        })
        .catch((error) => {
          const status = error?.response?.status;
          const code = error?.response?.data?.error;

          // GWB-411 hard lockout: hide the resend link until a fresh submission.
          if (code === "otp_locked") {
            this.locked = true;
            this.stopCountdown();
            this.canResend = false;
          }

          const messageKey = {
            otp_locked: "outerweb.guest-apply:errors:otp_locked",
            rate_limited: "outerweb.guest-apply:errors:rate_limited",
            email_failed: "outerweb.guest-apply:errors:email_failed",
            forbidden: "outerweb.guest-apply:errors:forbidden",
            invalid_state: "outerweb.guest-apply:errors:invalid_state",
          }[code];

          if (messageKey) {
            this.otp.errorMessage = this.$lang(messageKey);
          } else if (status === 429) {
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:rate_limited"
            );
          } else {
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:resend_failed"
            );
          }
        });
    },

    backToForm() {
      this.step = "form";
      this.stopCountdown();
      // Clear OTP-step state so an old banned/apiError banner doesn't follow
      // the user back into the form step from a previous attempt.
      this.bannedMessage = "";
      this.apiError = "";
      this.otp.errorMessage = "";
      this.otp.infoMessage = "";
      this.$nextTick(() => {
        const emailEl = this.$refs.emailField;
        if (emailEl && emailEl.$el) {
          const input = emailEl.$el.querySelector("input");
          if (input) input.focus();
        }
      });
    },

    // ─── Change-email modal (marketing "use another email") ────────────────────

    openEmailModal() {
      this.modalEmail = this.form.email;
      this.modalError = "";
      this.emailModalOpen = true;
    },

    closeEmailModal() {
      this.emailModalOpen = false;
      this.modalError = "";
    },

    submitEmailModal() {
      if (this.modalLoading) return;

      const email = this.modalEmail.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.modalError = this.$lang("outerweb.guest-apply:modal:invalid_email");
        return;
      }

      this.modalLoading = true;
      this.modalError = "";

      this.$http
        .post("/apply/update-email", { token: this.intakeToken, email })
        .then(() => {
          // A fresh code is on its way to the new address: update the banner email,
          // reset the OTP entry, restart the resend countdown, and confirm.
          this.form.email = email;
          this.emailModalOpen = false;
          this.clearOtpDigits();
          this.otp.errorMessage = "";
          this.bannedMessage = "";
          this.stopCountdown();
          this.countdown = 45;
          this.canResend = false;
          this.startCountdown();
          this.otp.infoMessage = this.$lang(
            "outerweb.guest-apply:errors:resend_success"
          );
        })
        .catch((error) => {
          const status = error && error.response && error.response.status;
          const code = error && error.response && error.response.data
            && error.response.data.error;
          // GWB-411 hard lockout: also a 422, so check the code before the
          // generic invalid-email branch. Close the modal — the lockout copy
          // belongs on the OTP step, where the resend link is now hidden.
          if (code === "otp_locked") {
            this.locked = true;
            this.stopCountdown();
            this.canResend = false;
            this.emailModalOpen = false;
            this.otp.errorMessage = this.$lang(
              "outerweb.guest-apply:errors:otp_locked"
            );
          } else if (status === 422) {
            this.modalError = this.$lang(
              "outerweb.guest-apply:modal:invalid_email"
            );
          } else if (status === 503) {
            this.modalError = this.$lang(
              "outerweb.guest-apply:errors:email_failed"
            );
          } else if (status === 429) {
            // Backend hit the shared resend rate limit — surface the existing
            // "too many, wait" copy instead of the generic failure.
            this.modalError = this.$lang(
              "outerweb.guest-apply:errors:rate_limited"
            );
          } else {
            this.modalError = this.$lang(
              "outerweb.guest-apply:errors:update_email_failed"
            );
          }
        })
        .finally(() => {
          this.modalLoading = false;
        });
    },

    // ─── Countdown ────────────────────────────────────────────────────────────

    startCountdown() {
      this.stopCountdown();
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1;
        } else {
          this.canResend = true;
          this.stopCountdown();
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    // ─── Funnel tracking ──────────────────────────────────────────────────────

    /**
     * GWB-411: shared properties on every guest_application:* Mixpanel event.
     * workbit_type is hardcoded — guest apply exists for Project jobs only
     * (GWB-481 scoped WorkSurfs out). user_language rides on the plugin's
     * registered super-prop.
     */
    guestMixpanelProps(extra = {}) {
      return Object.assign(
        {
          workbit_type: "project",
          workbit_id: String(this.jobId),
          user_country: this.userCountry,
          source:
            this.entryMode === "marketing"
              ? "marketing_landing"
              : "job_detail_page",
        },
        extra
      );
    },

    trackFunnelEvent(event) {
      const payload = { event, job_id: this.jobId };
      if (this.guestApplicationId) {
        payload.guest_application_id = this.guestApplicationId;
      }
      this.$http.post("/guest-funnel-event", payload).catch(() => {});
    },
  },
};
</script>

<style scoped>
.c-guest-apply-form {
  width: 100%;
}
.c-guest-apply-form__slide-enter-active,
.c-guest-apply-form__slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.c-guest-apply-form__slide-enter,
.c-guest-apply-form__slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.c-guest-apply-form__heading {
  margin-bottom: 1.25rem;
}

.c-guest-apply-form__helper {
  margin-top: -0.75rem;
  margin-bottom: 1.25rem;
  font-size: 0.9375rem;
  color: #555;
}

.c-guest-apply-form__otp-subheader {
  margin-bottom: 1rem;
  font-weight: 600;
}

/* PLAT-32 AC-2 — 44px touch target at 375px; shape mirrors the Alpine tooltip in
   canonical-job-card.blade.php, no gwbui dependency (GWB-494). */
.c-guest-apply-form__premium-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  cursor: help;
}

/* PLAT-32 AC-2 — the visible glyph (moon / calendar-star). The svg inherits the icon span's color via
   fill:currentColor; the modifier classes tint each type. */
.c-guest-apply-form__premium-icon-glyph {
  display: inline-flex;
  width: 1.25rem;
  height: 1.25rem;
}

.c-guest-apply-form__premium-icon-glyph svg {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

.c-guest-apply-form__premium-icon--night {
  color: #4a5568;
}

.c-guest-apply-form__premium-icon--holiday {
  color: #b7791f;
}

.c-guest-apply-form__premium-tooltip {
  position: absolute;
  z-index: 10;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 30ch;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.2;
  color: #fff;
  background: #333;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Centre the CTA button on mobile only (Foundation small breakpoint < 640px).
   On desktop the button stays in its natural left-aligned position inside the
   workbite-details column. */
@media (max-width: 639.98px) {
  .c-guest-apply-form__cta-step {
    text-align: center;
  }
}

.c-guest-apply-form__fieldset {
  border: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.c-guest-apply-form__legend {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.c-guest-apply-form__required {
  color: #c0392b;
}

.c-guest-apply-form__checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.375rem;
}

.c-guest-apply-form__checkbox-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: auto 0;
}

.c-guest-apply-form__checkbox-row--terms {
  margin-top: 1rem;
  margin-bottom: 0.25rem;
}

.c-guest-apply-form__checkbox-row--terms a {
  text-decoration: underline;
}

.c-guest-apply-form__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.c-guest-apply-form__banner {
  background-color: #6b3fa0;
  color: white;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.c-guest-apply-form__otp-error {
  display: block;
  margin-bottom: 0.75rem;
}

.c-guest-apply-form__otp-info {
  display: block;
  margin-bottom: 0.75rem;
  color: #2c7a3f;
  text-align: center;
}

.c-guest-apply-form__resend {
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
  text-align: center;
}

.c-guest-apply-form__resend-link--disabled {
  color: #aaa;
  pointer-events: none;
  text-decoration: none;
  cursor: default;
}

.c-guest-apply-form__trust {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  font-size: 0.8125rem;
  color: #6b3fa0;
}

.c-guest-apply-form__summary {
  margin-top: 1.5rem;
}

.c-guest-apply-form__summary-row {
  margin-bottom: 0.5rem;
}

/* Change-email modal (marketing "use another email") */
.c-guest-apply-form__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.c-guest-apply-form__modal {
  background: #fff;
  border-radius: 6px;
  padding: 1.5rem;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.c-guest-apply-form__modal-heading {
  margin-bottom: 1rem;
}
</style>
